/**
 * Issue #179 — 知识库条目工作区的纯逻辑 helper（便于单测）。
 *
 * 关注点：列隐藏只能落在后端白名单内、分页页大小限定 20/50/100、筛选状态到
 * 后端查询参数的映射。组件只负责状态与渲染，决策逻辑全部在这里且可测。
 */
import type { Difficulty, KnowledgeListQuery, QuestionType } from './types';

export const KNOWLEDGE_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
export const KNOWLEDGE_DEFAULT_PAGE_SIZE = 20;

export const KNOWLEDGE_DEFAULT_VISIBLE_COLUMNS = [
  'question',
  'type',
  'difficulty',
  'courseName',
  'categoryName',
  'isActive',
  'updatedAt',
];

/**
 * 计算可见列：优先使用持久化/默认集，但**永远**与后端白名单求交集，
 * 杜绝请求非白名单字段。交集为空时回退到白名单前若干列。
 */
export function resolveVisibleColumns(
  preferred: string[] | null | undefined,
  whitelist: string[],
  fallbackCount = 5,
): string[] {
  if (whitelist.length === 0) return [];
  const base =
    preferred && preferred.length > 0 ? preferred : KNOWLEDGE_DEFAULT_VISIBLE_COLUMNS;
  const intersection = base.filter((c) => whitelist.includes(c));
  if (intersection.length > 0) return intersection;
  return whitelist.slice(0, fallbackCount);
}

/**
 * 切换某列显隐。开启时按白名单顺序重排，保证列展示稳定；列必须在白名单内
 * 才会被加入（否则原样返回，绝不引入非白名单列）。
 */
export function toggleColumn(
  current: string[],
  col: string,
  whitelist: string[],
): string[] {
  if (current.includes(col)) return current.filter((c) => c !== col);
  if (!whitelist.includes(col)) return current;
  return whitelist.filter((c) => current.includes(c) || c === col);
}

/** 页大小限定在 20/50/100，非法值回退默认 20。 */
export function normalizePageSize(value: number): number {
  return (KNOWLEDGE_PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
    ? value
    : KNOWLEDGE_DEFAULT_PAGE_SIZE;
}

export interface KnowledgeFilterState {
  keyword: string;
  type: QuestionType | '';
  difficulty: Difficulty | '';
  courseId: string;
  categoryId: string;
  activeFilter: 'all' | 'true' | 'false';
  sortBy: 'sortOrder' | 'createdAt' | 'updatedAt';
  sortOrder: 'asc' | 'desc';
  page: number;
  pageSize: number;
  columns: string[];
}

/**
 * UI 筛选状态 → 后端查询参数。空字符串筛选项一律省略（不下发空过滤），
 * isActive 仅在显式选「启用/停用」时下发布尔，「全部」则省略。
 */
export function buildKnowledgeListQuery(
  state: KnowledgeFilterState,
): KnowledgeListQuery {
  return {
    keyword: state.keyword.trim() || undefined,
    type: state.type || undefined,
    difficulty: state.difficulty || undefined,
    courseId: state.courseId || undefined,
    categoryId: state.categoryId || undefined,
    isActive: state.activeFilter === 'all' ? undefined : state.activeFilter === 'true',
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
    page: state.page,
    pageSize: normalizePageSize(state.pageSize),
    columns: state.columns.length > 0 ? state.columns : undefined,
  };
}
